// Reharmonize (plan §8, entry point 3): ONE switch and a few levers.
//
// The switch turns a live reharmonization on and off; the levers — how far up
// the ladder, how far out, how much, and Another — say what it is. While the
// switch is on, every lever change re-derives underneath you, live, with the
// band playing; turning it off puts the harmony you started from back. That
// is the whole interaction (user ruling Sep 2026: "pull some levers, change
// some buttons and see the reharm happen in real time even if it's playing;
// if I want to go back I just turn it off" — A/B by one switch). It replaced a
// Try it / Keep / Cancel sequence whose kept settings never quite read back.
//
// Underneath, "on" IS the preview layer the store already knows how to hold
// (`reharmPreview`): a real layer on the real score, written without a history
// entry, always re-derived from its base so levers replace rather than stack,
// and dropped by any other edit (`endsReharmPreview`). Save turns the current
// one into an ordinary named layer for later; the switch then reads off,
// because what you hear is no longer a live derivation but a saved one — the
// layer list below is where saved harmonies are A/B'd against the original.
//
// The levers live in the store (`reharmSettings`, persisted): the panel
// unmounts with the drawer, and a lever that snaps back on reopen is not a
// lever. Scope is the selected bars when there are any, else the whole tune,
// and while the switch is on a new selection re-derives like any lever.

import { useEffect, useRef } from 'react';
import { passageLabel, type Passage } from '@/music/passage';
import { ADVENTURE_BANDS, type AdventureBand, type ReharmRung } from '@/music/reharm';
import { activeReharmLayer } from '@/music/score/reharm';
import { useStore } from '@/state/store';

const RUNGS: { rung: ReharmRung; label: string }[] = [
  { rung: 0, label: 'Colour' },
  { rung: 1, label: 'Same key' },
  { rung: 2, label: 'Dominants' },
  { rung: 3, label: 'Passing' },
  { rung: 4, label: 'Borrowed' },
  { rung: 5, label: 'Bass line' },
  { rung: 6, label: 'Coltrane' },
  { rung: 7, label: 'Modern' },
];

const BAND_LABEL: Record<AdventureBand, string> = {
  inside: 'Inside',
  stretched: 'Stretched',
  out: 'Out',
  free: 'Free',
};

/** A range input fires on every step, and one derivation re-voices the whole
 *  tune — so a drag settles before anything is heard. */
const SETTLE_MS = 150;

const scopeKey = (passage: Passage | null) =>
  passage ? `${passage.fromMeasureId}>${passage.toMeasureId}` : 'all';

export function ReharmPassagePanel() {
  const score = useStore((s) => s.score);
  const passage = useStore((s) => s.passage);
  const preview = useStore((s) => s.reharmPreview);
  const settings = useStore((s) => s.reharmSettings);
  const setReharmSettings = useStore((s) => s.setReharmSettings);
  const previewReharmPassage = useStore((s) => s.previewReharmPassage);
  const keepReharmPreview = useStore((s) => s.keepReharmPreview);
  const cancelReharmPreview = useStore((s) => s.cancelReharmPreview);

  const on = preview !== null;
  // The scope is a lever too: the selected bars are what gets reharmonised.
  const levers = `${settings.maxRung}:${settings.adventure}:${settings.density}:${settings.variation}`;
  const key = `${levers}:${scopeKey(passage)}`;

  // Selecting a SAVED layer while the switch is off moves the levers to the
  // settings that made it — reading what is on the form, not re-deriving.
  // While the switch is on the levers are yours and nothing moves them.
  // Leaving an audition for the layer it was derived from is the audition
  // ending (the switch going off, an edit), not a choice of layer: the levers
  // stay where you left them, so on again gives the same reharm back.
  const shownLayer = useRef(activeReharmLayer(score));
  useEffect(() => {
    const layer = activeReharmLayer(score);
    const was = shownLayer.current;
    // Kept up to date even when the id holds: Save keeps the audition's id
    // and only drops its preview flag.
    shownLayer.current = layer;
    if ((layer?.id ?? null) === (was?.id ?? null)) return;
    if (was?.preview && was.derivedFrom === (layer?.id ?? null)) return;
    if (on || !layer?.derivation || layer.preview) return;
    setReharmSettings(layer.derivation);
  }, [score, on, setReharmSettings]);

  // Re-derive whenever a lever moves or the selected bars change, but only
  // while the switch is on: a lever moved with the switch off is set for next
  // time and changes nobody's chords. The store reads the passage itself.
  // The audition outlives the drawer, so the bars can change while the panel
  // is shut: it opens against the scope the audition covers and picks up a
  // new selection as if it had been made here.
  // `lastKey` is what the audition was derived at, so it moves only when the
  // derivation runs: a cancelled timer (StrictMode's doubled first pass, or a
  // drag still moving) must leave the change outstanding.
  const lastKey = useRef(preview ? `${levers}:${scopeKey(preview.passage)}` : key);
  useEffect(() => {
    if (lastKey.current === key) return;
    if (!on) {
      lastKey.current = key;
      return;
    }
    const timer = setTimeout(() => {
      lastKey.current = key;
      previewReharmPassage(settings);
    }, SETTLE_MS);
    return () => clearTimeout(timer);
    // `settings` and the scope are what `key` identifies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, on, previewReharmPassage]);

  const toggle = (next: boolean) => {
    if (next) previewReharmPassage(settings);
    else cancelReharmPreview();
  };

  const scope = passage
    ? passageLabel(
        score.form.map((m) => m.id),
        passage,
      )
    : null;
  const count = preview?.count ?? 0;

  return (
    <div className="reharm-passage" data-testid="reharm-passage" data-on={on ? 'true' : 'false'}>
      <div className="reharm-passage-head">
        <label className="switch reharm-switch">
          <input
            type="checkbox"
            role="switch"
            checked={on}
            aria-checked={on}
            aria-label="Reharmonise"
            data-testid="reharm-switch"
            onChange={(e) => toggle(e.target.checked)}
          />
          <strong>Reharmonise</strong>
        </label>
        <span className="muted" data-testid="reharm-passage-scope">
          {scope ?? 'whole tune'}
        </span>
      </div>
      <label className="reharm-passage-row">
        <span>Up to</span>
        <select
          value={settings.maxRung}
          aria-label="Highest rung"
          data-testid="reharm-passage-rung"
          onChange={(e) => setReharmSettings({ maxRung: Number(e.target.value) as ReharmRung })}
        >
          {RUNGS.map((r) => (
            <option key={r.rung} value={r.rung}>
              {r.label}
            </option>
          ))}
        </select>
      </label>
      <div className="reharm-passage-row" role="group" aria-label="How far out">
        <span>How far out</span>
        <div className="segmented">
          {ADVENTURE_BANDS.map((band) => (
            <button
              key={band}
              type="button"
              className={settings.adventure === band ? 'active' : ''}
              aria-pressed={settings.adventure === band}
              data-testid={`reharm-adventure-${band}`}
              onClick={() => setReharmSettings({ adventure: band })}
            >
              {BAND_LABEL[band]}
            </button>
          ))}
        </div>
      </div>
      <label className="reharm-passage-row">
        <span>How much</span>
        <input
          type="range"
          min={0.1}
          max={1}
          step={0.1}
          value={settings.density}
          aria-label="How many chords change"
          data-testid="reharm-passage-density"
          onChange={(e) => setReharmSettings({ density: Number(e.target.value) })}
        />
        <span className="muted">{Math.round(settings.density * 100)}%</span>
      </label>
      <div className="reharm-passage-actions">
        <button
          type="button"
          className="btn btn-sm"
          data-testid="reharm-passage-another"
          onClick={() => setReharmSettings({ variation: settings.variation + 1 })}
        >
          Another
        </button>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="reharm-passage-keep"
          disabled={!on || count === 0}
          onClick={() => keepReharmPreview()}
        >
          Save
        </button>
      </div>
      {on && (
        <p className="panel-hint" data-testid="reharm-passage-result">
          {count === 0
            ? 'No changes at this setting'
            : `${count} new chord${count === 1 ? '' : 's'}`}
        </p>
      )}
    </div>
  );
}
